import * as React from "react";
import { Form, Label, List } from "semantic-ui-react";
import { Address } from "./Address";
import ErrorMessage from "../Validators/ErrorMessage";
import debounce from "lodash/debounce.js";
import CountrySelector from "../CountrySelector/CountrySelector";
import { Country } from "../CountrySelector/Country";
import { PostCodeFindResponse } from "./PostCodeFindResponse";
import { validation } from "../../util/validation";
import styled from "styled-components";

const StyledAddressSelectorContainer = styled.div`
  .postcodeSearchSwitchMode {
    background: none !important;
    color: cornflowerblue !important;
  }

  .postCodeLookupFieldLabel {
    background: none !important;
    color: #5f7183 !important;
    font-weight: 400 !important;
    display: block !important;
    font-size: 0.92857143em !important;
    padding-left: 0 !important;
  }

  .postcodeSearchChangeCountrySelection {
    background: none !important;
    color: cornflowerblue !important;
  }

  .ui.list.postcodeSearchResultsList {
    max-height: 210px;
    overflow-y: auto;
    border: 1px solid #ccc;
    margin-bottom: 0.5em !important;
    margin-top: 8px !important;
    min-height: 110px;
    background: #f5f8f9;
  }

  .ui.list.postcodeSearchResultsList .item {
    border-radius: 0 !important;
    background: #fff;
  }

  .ui.list.postcodeSearchResultsList .imgSpan {
    height: 20px;
    display: inline-block;
    vertical-align: middle;
    padding-right: 0.5em;
  }

  .ui.list.postcodeSearchResultsList .txtSpan {
    display: inline-block;
    vertical-align: middle;
    font-size: 0.8em;
  }

  .postCodeLookupFormattedAddress {
    margin: 0 0 0.5em 0 !important;
    font-size: 0.9em !important;
    box-shadow: none !important;
  }

  .postcodeFormattedAddress {
    white-space: pre-wrap;
    border: 1px solid #e0e0e0 !important;
    padding: 10px !important;
    margin-bottom: 8px !important;
    background: #f5f8f9;
  }

  .postcodeSearchResetButton,
  .postcodeSearchEditAddressButton,
  .postcodeSearchEditNonUKAddressButton {
    background: none !important;
    text-decoration: underline !important;
    padding: 0 8px 0 0 !important;
    color: #103f6f !important;
    font-size: 1em !important;
    margin-bottom: 24px !important;
  }

  .postcodeManualAddressControl .ui.label {
    display: inline-block;
    margin-bottom: 8px;
  }

  .postcodeManualAddressControl .ui.input {
    display: block;
    margin-bottom: 8px;
  }

  .postcodeLookupAddressControl .ui.input {
    display: block;
    margin-bottom: 0;
  }

  .postcodeSearchSwitchMode {
    margin-bottom: 8px;
    display: block;
  }

  .inputPostcode,
  .inputCountry {
    display: block;
  }

  .inputPostcode .ui.input > input {
    width: 100% !important;
    margin-bottom: 0 !important;
  }

  .address-reset-button,
  .address-edit-button,
  .address-cancel-button,
  .address-confirm-button {
    background: none !important;
    text-decoration: underline !important;
    padding: 0 8px 0 0 !important;
    color: #103f6f !important;
    font-size: 1em !important;
    margin-bottom: 24px !important;
  }
`;

export enum DisplayMode {
  SeparateAddressFields,
  FormattedAddress,
  Lookup,
}

export enum EditMode {
  ReadOnly,
  AllowChangeToCountryAndAddress,
  AllowChangeToAddressOnly,
}

interface IAddressSelectorRequiredProps {
  onAddressSelect(address: any): void;

  onReset(): void;

  onInvalidAddress(): void;

  onValidAddress(): void;

  onDisplayModeChange(dm: DisplayMode): void;

  onCountryChange(c: Country): void;

  address?: Address;
}

interface IAddressSelectorOptionalProps {
  allowInternationalAddress: boolean;
  searchCountries: string;
  postCodePrefixesToBlock: string;

  blockedPostCodeCallback(
    searchTerm: string,
    postCodePrefixToBlock: string,
    msg: string,
  ): void;

  className: string;
  showDebug: boolean;
  baseURL: string;
  findMatchingPostCodesEndpoint: string;
  findAddressesAtLocationEndpoint: string;
  getPropertyDetailsEndpoint: string;
  initialDisplayMode: DisplayMode;
  editMode: EditMode;
  showCountry: boolean;
}

interface IAddressSelectorProps
  extends IAddressSelectorRequiredProps, IAddressSelectorOptionalProps {}

interface IAddressSelectorState {
  address: Address;
  savedAddress: Address;
  savedDisplayMode: DisplayMode;
  selectedAddress: Address | null;
  selectedCountry: Country;
  defaultCountry: Country;
  currentDisplayMode: DisplayMode;
  isLoading: boolean;
  isInternationalAddress: boolean;
  hasSelectedAddress: boolean;
  hasSelectedCountry: boolean;
  addressSearchTerm: string;
  formattedAddress: string;
  showAddress1MinLengthError: boolean;
  showAddress1Error: boolean;
  showAddress2MinLengthError: boolean;
  showAddress2Error: boolean;
  showCityMinLengthError: boolean;
  showCityError: boolean;
  showCountyMinLengthError: boolean;
  showCountyError: boolean;
  showCountryMinLengthError: boolean;
  showCountryError: boolean;
  showPostCodeError: boolean;
  showEmptyAddressError: boolean;
  showAddressSearchError: boolean;
  results: PostCodeFindResponse[];
  showBlockedPostCodeError: boolean;
}

class AddressSelector extends React.Component<
  IAddressSelectorProps,
  IAddressSelectorState
> {
  countrySelectorRef = React.createRef<CountrySelector>();

  static defaultProps = {
    initialDisplayMode: DisplayMode.Lookup,
    editMode: EditMode.AllowChangeToCountryAndAddress,
    allowInternationalAddress: false,
    searchCountries: "GBR",
    baseURL: "https://addresslookup.alanboswell.com/addresslookup",
    findMatchingPostCodesEndpoint: "/postcode",
    findAddressesAtLocationEndpoint: "/address",
    getPropertyDetailsEndpoint: "/details",
    postCodePrefixesToBlock: "",
    className: "",
    showDebug: false,
    showCountry: false,
  };

  state: IAddressSelectorState = {
    address: this.props.address ? this.props.address : new Address(),
    savedAddress: this.props.address ? this.props.address : new Address(),
    selectedAddress: this.props.address ? this.props.address : new Address(),
    selectedCountry: Object.assign(new Country(), {
      diallingCode: "44",
      alpha2Code: "GB",
      alpha3Code: "GBR",
      numeric: 826,
      englishShortName: "United Kingdom",
      currencyName: "Pound Sterling",
      currencyCode: "GBP",
    }),
    defaultCountry: Object.assign(new Country(), {
      diallingCode: "44",
      alpha2Code: "GB",
      alpha3Code: "GBR",
      numeric: 826,
      englishShortName: "United Kingdom",
      currencyName: "Pound Sterling",
      currencyCode: "GBP",
    }),
    currentDisplayMode: this.props.initialDisplayMode,
    savedDisplayMode: this.props.initialDisplayMode,
    isInternationalAddress: this.props.showCountry,
    isLoading: true,
    hasSelectedAddress: !!this.props.address,
    hasSelectedCountry: true,
    addressSearchTerm: "",
    formattedAddress: "",
    showAddress1MinLengthError: false,
    showAddress1Error: false,
    showAddress2MinLengthError: false,
    showAddress2Error: false,
    showCityMinLengthError: false,
    showCityError: false,
    showCountyMinLengthError: false,
    showCountyError: false,
    showCountryMinLengthError: false,
    showCountryError: false,
    showPostCodeError: false,
    showEmptyAddressError: false,
    showAddressSearchError: false,
    showBlockedPostCodeError: false,
    results: [],
  };

  //
  // Call findMatchingPostCodes using debounce to reduce the number of calls made
  //

  findMatchingPostCodesDebounced = debounce(
    (searchTerm: string) => {
      if (this.props.postCodePrefixesToBlock.length > 0) {
        let listOfPostCodePrefixes =
          this.props.postCodePrefixesToBlock.split(";");
        let blockApplied = false;

        for (let i = 0; i < listOfPostCodePrefixes.length; i++) {
          if (!blockApplied) {
            if (
              searchTerm
                .toUpperCase()
                .startsWith(listOfPostCodePrefixes[i].toUpperCase())
            ) {
              // BLOCK
              if (typeof this.props.blockedPostCodeCallback === "function") {
                this.props.blockedPostCodeCallback(
                  searchTerm,
                  listOfPostCodePrefixes[i],
                  "Post codes beginning with " +
                    listOfPostCodePrefixes[i] +
                    " are not permitted.",
                );
                this.setState({ showBlockedPostCodeError: true });
                blockApplied = true;
              }
            }
          }
        }

        this.setState({ showBlockedPostCodeError: blockApplied });

        if (blockApplied) {
          this.setState({ results: [] });
        } else {
          this.findMatchingPostCodes(searchTerm);
        }
      } else {
        this.findMatchingPostCodes(searchTerm);
      }
    },
    1000,
    {
      leading: true,
      trailing: true,
    },
  );

  postAjaxCall = (url: string, data: any) => {
    return new Promise(function (resolve, reject) {
      let req = new XMLHttpRequest();
      req.open("POST", url);

      req.setRequestHeader("Content-Type", " application/json;charset=utf-8");
      req.onload = function () {
        if (req.status >= 200 && req.status < 300) {
          resolve(req.response);
        } else {
          reject(req.statusText);
        }
      };

      req.onerror = function () {
        reject(Error("Network Error"));
      };
      req.send(JSON.stringify(data));
    });
  };

  //
  // Searches for matching post codes based on the search string
  // Note : This may return individual addresses also that match the search string
  //

  findMatchingPostCodes = (searchTerm: string) => {
    if (this.props.showDebug) {
      console.log(
        "AddressSelector::findMatchingPostCodes():search parameters",
        {
          text: searchTerm.toLowerCase(),
          countries: this.state.selectedCountry.alpha3Code,
        },
      );
    }

    const url = this.props.baseURL + this.props.findMatchingPostCodesEndpoint;

    this.postAjaxCall(url, {
      text: searchTerm.toLowerCase(),
      countries: this.state.selectedCountry.alpha3Code,
    }).then((data) => {
      if (typeof data === "string") {
        let matches = JSON.parse(data);
        this.setState({ results: matches, isLoading: false });
      }
    });
  };

  //
  // onChange handler for the address search box
  //

  editAddressSearchTerm = (e: { target: { value: any } }) => {
    let searchText = e.target ? e.target.value : "";
    this.setState({ addressSearchTerm: searchText }, () => {
      if (this.state.addressSearchTerm.split(" ").join().length > 1) {
        this.findMatchingPostCodesDebounced(this.state.addressSearchTerm);
      } else {
        this.setState({ results: [] });
      }
    });
  };

  //
  // onClick handler for the list of address/property items returned from a search
  //

  selectAddressEntry = (idSelected: string) => {
    // Find the entry we clicked on
    let chosenEntry = this.state.results.find((result) => {
      return result.id === idSelected;
    });

    if (chosenEntry) {
      if (this.props.showDebug) {
        console.log(
          "AddressSelector::selectAddressEntry():address entry selected " +
            idSelected +
            ", type=" +
            chosenEntry.type,
        );
      }

      const addressesAtLocationUrl =
        this.props.baseURL + this.props.findAddressesAtLocationEndpoint;
      const propertyDetailsUrl =
        this.props.baseURL + this.props.getPropertyDetailsEndpoint;

      // If this not an address, we need to find addresses at this location
      if (chosenEntry.type.toUpperCase() !== "ADDRESS") {
        this.postAjaxCall(addressesAtLocationUrl, chosenEntry).then((data) => {
          if (typeof data === "string") {
            let matches = JSON.parse(data);
            this.setState({ results: matches });
          }
        });
      } else {
        // This is an an address, so now get the details of the property
        this.postAjaxCall(propertyDetailsUrl, chosenEntry).then((data) => {
          if (typeof data === "string") {
            let propertyDetails = JSON.parse(data);

            let county = propertyDetails.city; // default to  city

            if (propertyDetails.adminAreaName != "") {
              county = propertyDetails.adminAreaName; // use County if its there
            } else {
              if (propertyDetails.line3 != "") {
                county = propertyDetails.line3; // try line 3 otherwise
              }
            }

            let addr: Address = {
              address1: propertyDetails.line1,
              address2: propertyDetails.line2,
              county: county,
              postCode: propertyDetails.postalCode,
              city: propertyDetails.city,
              country: propertyDetails.countryName,
              country2CharIsoCode: propertyDetails.countryIso2,
              formatted: "",
            };

            let blockApplied = false;
            if (this.props.postCodePrefixesToBlock.length > 0) {
              let listOfPostCodePrefixes =
                this.props.postCodePrefixesToBlock.split(";");

              for (let i = 0; i < listOfPostCodePrefixes.length; i++) {
                if (!blockApplied) {
                  if (
                    addr.postCode
                      .toUpperCase()
                      .startsWith(listOfPostCodePrefixes[i].toUpperCase())
                  ) {
                    // BLOCK
                    if (
                      typeof this.props.blockedPostCodeCallback === "function"
                    ) {
                      this.props.blockedPostCodeCallback(
                        addr.postCode,
                        listOfPostCodePrefixes[i],
                        "Post codes beginning with " +
                          listOfPostCodePrefixes[i] +
                          " are not permitted.",
                      );
                      this.setState({ showBlockedPostCodeError: true });
                      blockApplied = true;
                    }
                  }
                }
              }

              this.setState({ showBlockedPostCodeError: blockApplied });

              if (blockApplied) {
                this.setState({ results: [] });
              }
            }

            if (!blockApplied) {
              addr.formatted = this.formatAddress(addr);

              this.setState(
                {
                  results: propertyDetails,
                  selectedAddress: addr,
                  address: addr,
                },
                () => {
                  if (this.validate()) {
                    this.setState(
                      {
                        hasSelectedAddress: true,
                        currentDisplayMode: DisplayMode.FormattedAddress,
                      },
                      () => {
                        this.fireDisplayModeChange(
                          DisplayMode.FormattedAddress,
                        );
                        this.fireIfValid();
                      },
                    );
                  } else {
                    this.setState(
                      {
                        currentDisplayMode: DisplayMode.SeparateAddressFields,
                      },
                      () => {
                        this.fireDisplayModeChange(
                          DisplayMode.SeparateAddressFields,
                        );
                      },
                    );
                  }
                },
              );
            }
          }
        });
      }
    }
  };

  fireDisplayModeChange = (currentDisplayMode: DisplayMode) => {
    if (this.props.showDebug) {
      console.log(
        "AddressSelector::fireDisplayModeChange: " +
          DisplayMode[currentDisplayMode],
      );
    }
    this.props.onDisplayModeChange(currentDisplayMode);
  };

  resetAddressSelection = () => {
    if (this.props.showDebug) {
      console.log("AddressSelector::resetAddressSelection()");
    }
    this.setState(
      {
        selectedAddress: new Address(),
        address: new Address(),
        savedAddress: new Address(),
        addressSearchTerm: "",
        hasSelectedAddress: false,
        showBlockedPostCodeError: false,
        isLoading: true,
        currentDisplayMode: DisplayMode.Lookup,
      },
      () => {
        this.setState(
          {
            address: {
              ...this.state.address,
              country: this.state.defaultCountry.englishShortName,
              country2CharIsoCode: this.state.defaultCountry.alpha2Code,
            },
          },
          () => {
            this.fireDisplayModeChange(DisplayMode.Lookup);
            this.props.onReset();
          },
        );
      },
    );
  };

  onCountrySelect = (country: Country) => {
    if (this.props.showDebug) {
      console.log(
        "AddressSelector::onCountrySelect(): " + country.englishShortName,
      );
    }
    this.setState(
      {
        selectedCountry: country,
        hasSelectedCountry: true,
        defaultCountry: country, // Setting this prevents the country from changing on >address< reset
      },
      () => {
        this.resetAddressSelection();
        this.props.onCountryChange(this.state.selectedCountry);

        if (this.props.showDebug) {
          console.log(
            "AddressSelector::onCountrySelect():selectedCountry now set to",
            this.state.selectedCountry.englishShortName,
          );
          console.log(
            "AddressSelector::onCountrySelect():selectedCountry dialling code now set to +",
            this.state.selectedCountry.diallingCode,
          );
        }
      },
    );
  };

  onCountryReset = () => {
    if (this.props.showDebug) {
      console.log("AddressSelector::onCountryReset()");
    }
    this.setState(
      {
        hasSelectedCountry: false,

        isLoading: true,
      },
      () => {},
    );
  };

  validateCountry = (): boolean => {
    if (!this.countrySelectorRef.current) {
      if (this.props.showDebug) {
        console.log(
          "AddressSelector::validateCountry():Could not get reference to country selector, returning valid",
        );
      }
      return true;
    }

    return this.countrySelectorRef.current.validate();
  };

  fireIfValid = () => {
    if (this.validate()) {
      this.props.onAddressSelect(this.state.address);
    } else {
      this.setState(
        {
          currentDisplayMode: DisplayMode.SeparateAddressFields,
        },
        () => {
          this.fireDisplayModeChange(DisplayMode.SeparateAddressFields);
        },
      );
    }
  };

  renderCountryControl = () => {
    return (
      <CountrySelector
        baseURL={this.props.baseURL}
        country={this.state.selectedCountry}
        searchCountries={this.props.searchCountries}
        ref={this.countrySelectorRef}
        onReset={this.onCountryReset}
        onCountrySelect={this.onCountrySelect}
        alwaysShowCountryList={false}
        disabled={false}
        showDebug={this.props.showDebug}
      />
    );
  };

  renderEmbeddedDisabledCountryControl = () => {
    return (
      <CountrySelector
        country={this.state.selectedCountry}
        baseURL={this.props.baseURL}
        searchCountries={this.props.searchCountries}
        onReset={this.onCountryReset}
        ref={this.countrySelectorRef}
        disabled={true}
        onCountrySelect={() => {}}
        alwaysShowCountryList={true}
        showDebug={this.props.showDebug}
      />
    );
  };

  renderFormattedAddressControl = () => {
    return (
      <div className="postcodeFormattedAddressControl">
        {this.state.hasSelectedAddress && (
          <div className="postcodeFormattedAddress">
            {this.formatAddress(this.state.address)}
          </div>
        )}
        {this.props.editMode != EditMode.ReadOnly &&
          this.state.hasSelectedAddress && (
            <Label
              className="postcodeSearchResetButton"
              as="a"
              onClick={() => {
                this.resetAddressSelection();
              }}
            >
              Reset
            </Label>
          )}
        {this.props.editMode != EditMode.ReadOnly &&
          this.state.hasSelectedAddress && (
            <Label
              className="postcodeSearchEditAddressButton"
              as="a"
              onClick={(event: any) => {
                event.preventDefault();
                this.setState(
                  {
                    savedDisplayMode: DisplayMode.FormattedAddress,
                    savedAddress: this.state.address,
                    currentDisplayMode: DisplayMode.SeparateAddressFields,
                  },
                  () => {
                    this.fireDisplayModeChange(
                      DisplayMode.SeparateAddressFields,
                    );
                  },
                );
              }}
            >
              Edit
            </Label>
          )}
      </div>
    );
  };

  renderLookupAddressControl = () => {
    return (
      <div className="postcodeLookupAddressControl">
        {this.props.allowInternationalAddress &&
          this.props.editMode === EditMode.AllowChangeToCountryAndAddress &&
          (this.state.isInternationalAddress ||
            this.state.selectedCountry.alpha2Code !== "GB") &&
          this.renderCountryControl()}
        {this.props.allowInternationalAddress &&
          this.props.editMode !== EditMode.AllowChangeToCountryAndAddress &&
          this.state.selectedCountry.alpha2Code !== "GB" &&
          this.renderEmbeddedDisabledCountryControl()}
        {!this.state.hasSelectedAddress && this.state.hasSelectedCountry && (
          <React.Fragment>
            <Form.Input
              autoComplete="off"
              placeholder="Start typing your address and select from the list"
              className="no_label has_tooltip inputPostcode"
              type="search"
              name="addressSearch"
              error={this.state.showEmptyAddressError}
              value={this.state.addressSearchTerm}
              onChange={this.editAddressSearchTerm}
            />
            {this.state.showBlockedPostCodeError && (
              <List
                className="postcodeSearchResultsList"
                name="postcodeList"
                selection
                verticalAlign="middle"
              >
                <React.Fragment>
                  <List.Item>
                    <List.Content>
                      <List.Header>
                        <span className="txtSpan">
                          We are unable to accept this postcode region. Please
                          call us for more information.
                        </span>
                      </List.Header>
                    </List.Content>
                  </List.Item>
                </React.Fragment>
              </List>
            )}
            {!this.state.showBlockedPostCodeError && (
              <List
                className="postcodeSearchResultsList"
                name="postcodeList"
                selection
                verticalAlign="middle"
              >
                {!this.state.isLoading &&
                  this.state.results.length > 0 &&
                  !this.state.hasSelectedAddress &&
                  this.state.hasSelectedCountry && (
                    <React.Fragment>
                      {this.state.results.map((d) => (
                        <List.Item
                          key={d.id}
                          disabled={this.state.hasSelectedAddress}
                          onClick={() => {
                            this.selectAddressEntry(d.id);
                          }}
                        >
                          <List.Content>
                            <List.Header>
                              <span className="txtSpan">
                                {d.text} {d.description}
                              </span>
                            </List.Header>
                          </List.Content>
                        </List.Item>
                      ))}
                    </React.Fragment>
                  )}
              </List>
            )}
          </React.Fragment>
        )}

        {this.props.editMode != EditMode.ReadOnly &&
          !this.state.hasSelectedAddress &&
          this.state.hasSelectedCountry && (
            <Label
              className="postcodeSearchEditAddressButton"
              as="a"
              onClick={(event: any) => {
                event.preventDefault();
                this.setState(
                  {
                    savedDisplayMode: DisplayMode.Lookup,
                    currentDisplayMode: DisplayMode.SeparateAddressFields,
                    savedAddress: this.state.address,
                  },
                  () => {
                    this.fireDisplayModeChange(
                      DisplayMode.SeparateAddressFields,
                    );
                    this.props.onReset();
                  },
                );
              }}
            >
              Enter your address manually
            </Label>
          )}
        {this.props.editMode != EditMode.ReadOnly &&
          !this.state.hasSelectedAddress &&
          this.props.allowInternationalAddress &&
          this.state.hasSelectedCountry &&
          this.state.selectedCountry.alpha2Code == "GB"}
      </div>
    );
  };

  renderManualAddressControl = () => {
    return (
      <div className="postcodeManualAddressControl">
        {this.props.allowInternationalAddress &&
          this.props.editMode == EditMode.AllowChangeToCountryAndAddress &&
          this.renderCountryControl()}
        {this.props.allowInternationalAddress &&
          this.props.editMode != EditMode.AllowChangeToCountryAndAddress &&
          this.renderEmbeddedDisabledCountryControl()}
        <Form.Field>
          <Form.Input
            type="text"
            maxLength={39}
            className="postcodeManualAddressControlAddress1"
            error={
              this.state.showAddress1Error ||
              this.state.showAddress1MinLengthError
            }
            placeholder="Address 1"
            defaultValue={this.state.address.address1}
            onChange={(event: any) => {
              this.setState({
                address: {
                  ...this.state.address,
                  address1: event.target.value,
                },
                showAddress1Error: event.target.value.length <= 2,
              });
            }}
            onBlur={(event: any) => {
              if (event.target.value.length > 2) {
                event.target.value =
                  event.target.value[0].toUpperCase() +
                  event.target.value.slice(1, event.target.value.length);
              }
            }}
          />
          <ErrorMessage
            show={
              this.state.showAddress1MinLengthError ||
              this.state.showAddress1Error
            }
            message="Address 1 should have 2 or more letters/numbers"
          />
        </Form.Field>
        <Form.Field>
          <Form.Input
            type="text"
            placeholder="Address 2"
            className="postcodeManualAddressControlAddress2"
            maxLength={39}
            defaultValue={this.state.address.address2}
            onChange={async (event: any) => {
              await this.setState({
                address: {
                  ...this.state.address,
                  address2: event.target.value,
                },
              });
            }}
            onBlur={(event: any) => {
              if (event.target.value.length > 2) {
                event.target.value =
                  event.target.value[0].toUpperCase() +
                  event.target.value.slice(1, event.target.value.length);
              }
            }}
          />
          <ErrorMessage
            show={
              this.state.showAddress2MinLengthError ||
              this.state.showAddress2Error
            }
            message="Address 2 should have 2 or more letters/numbers"
          />{" "}
        </Form.Field>
        <Form.Field>
          <Form.Input
            type="text"
            error={this.state.showCityError}
            placeholder="City"
            className="postcodeManualAddressControlCity"
            defaultValue={this.state.address.city}
            maxLength={39}
            onChange={async (event: any) => {
              await this.setState({
                address: {
                  ...this.state.address,
                  city: event.target.value,
                },
                showCityError: event.target.value.length <= 2,
              });
            }}
            onBlur={(event: any) => {
              if (event.target.value.length > 2) {
                event.target.value =
                  event.target.value[0].toUpperCase() +
                  event.target.value.slice(1, event.target.value.length);
              }
            }}
          />
          <ErrorMessage
            show={this.state.showCityMinLengthError || this.state.showCityError}
            message="City/Town should have 2 or more letters"
          />{" "}
        </Form.Field>
        <Form.Field>
          <Form.Input
            type="text"
            placeholder="County"
            className="postcodeManualAddressControlCounty"
            maxLength={27}
            defaultValue={this.state.address.county}
            error={
              this.state.showCountyMinLengthError || this.state.showCountyError
            }
            onChange={async (event: any) => {
              await this.setState({
                address: {
                  ...this.state.address,
                  county: event.target.value,
                },
              });
            }}
            onBlur={(event: any) => {
              if (event.target.value.length > 2) {
                event.target.value =
                  event.target.value[0].toUpperCase() +
                  event.target.value.slice(1, event.target.value.length);
              }
            }}
          />
          <ErrorMessage
            show={
              this.state.showCountyMinLengthError || this.state.showCountyError
            }
            message="County should have 2 or more letters/numbers"
          />{" "}
        </Form.Field>
        <Form.Field>
          <Form.Input
            type="text"
            error={this.state.showPostCodeError}
            placeholder="Postcode"
            className="postcodeManualAddressControlPostCode"
            defaultValue={this.state.address.postCode}
            maxLength={8}
            minLength={5}
            onChange={async (event: any) => {
              await this.setState({
                address: {
                  ...this.state.address,
                  postCode: event.target.value,
                },
                showPostCodeError: event.target.value.length <= 4,
              });
            }}
            onBlur={(event: any) => {
              event.target.value = event.target.value.toUpperCase();
              if (event.target.value.length > 4) {
                event.target.value =
                  event.target.value
                    .slice(0, event.target.value.length - 3)
                    .trim() +
                  " " +
                  event.target.value
                    .slice(
                      event.target.value.length - 3,
                      event.target.value.length,
                    )
                    .trim();
              }
              let postCodeToCheck = this.state.address.postCode;
              let blockApplied = false;
              if (this.props.postCodePrefixesToBlock.length > 0) {
                let listOfPostCodePrefixes =
                  this.props.postCodePrefixesToBlock.split(";");
                for (let i = 0; i < listOfPostCodePrefixes.length; i++) {
                  if (!blockApplied) {
                    if (
                      postCodeToCheck
                        .toUpperCase()
                        .startsWith(listOfPostCodePrefixes[i].toUpperCase())
                    ) {
                      // BLOCK
                      if (
                        typeof this.props.blockedPostCodeCallback === "function"
                      ) {
                        this.props.blockedPostCodeCallback(
                          postCodeToCheck,
                          listOfPostCodePrefixes[i],
                          "Post codes beginning with " +
                            listOfPostCodePrefixes[i] +
                            " are not permitted.",
                        );
                        this.setState({ showBlockedPostCodeError: true });
                        blockApplied = true;
                      }
                    }
                  }
                }
                this.setState({ showBlockedPostCodeError: blockApplied });
              }
            }}
          />
          <ErrorMessage
            message="Post code should have 5 or more letters/numbers"
            show={this.state.showPostCodeError}
          />
          <ErrorMessage
            message="We are unable to accept this post code region. Please call us for more information."
            show={this.state.showBlockedPostCodeError}
          />
          <Label
            className="address-cancel-button"
            as="a"
            onClick={() => {
              this.setState(
                {
                  address: this.state.savedAddress,
                  selectedCountry: this.state.defaultCountry,
                  hasSelectedCountry: true,
                  isInternationalAddress: false,
                  currentDisplayMode: this.state.savedDisplayMode,
                  showAddress1MinLengthError: false,
                  showAddress1Error: false,
                  showAddress2MinLengthError: false,
                  showAddress2Error: false,
                  showCityMinLengthError: false,
                  showCityError: false,
                  showCountyMinLengthError: false,
                  showCountyError: false,
                  showCountryMinLengthError: false,
                  showCountryError: false,
                  showPostCodeError: false,
                  showEmptyAddressError: false,
                  showAddressSearchError: false,
                },
                () => {
                  this.props.onDisplayModeChange(this.state.savedDisplayMode);
                },
              );
            }}
          >
            Cancel
          </Label>
          <Label
            className="address-confirm-button"
            as="a"
            onClick={() => {
              if (this.validate()) {
                this.setState(
                  {
                    currentDisplayMode: DisplayMode.FormattedAddress,
                    hasSelectedAddress: true,
                    address: {
                      ...this.state.address,
                      formatted: this.formatAddress(this.state.address),
                    },
                  },
                  () => {
                    if (this.props.showDebug) {
                      console.log(
                        "AddressSelector::Manual address entry valid : changing to Formatted Address",
                      );
                      console.log(
                        "AddressSelector::Manual address entry valid : address set to",
                        this.state.address,
                      );
                    }
                    this.props.onDisplayModeChange(
                      DisplayMode.FormattedAddress,
                    );
                    this.props.onAddressSelect(this.state.address);
                  },
                );
              }
            }}
          >
            Confirm
          </Label>
        </Form.Field>
      </div>
    );
  };

  renderAddressSearchControl = () => {
    return (
      <div className="addressSelectorContainer">
        {this.state.currentDisplayMode == DisplayMode.FormattedAddress &&
          this.renderFormattedAddressControl()}

        {this.state.currentDisplayMode == DisplayMode.Lookup &&
          this.renderLookupAddressControl()}

        {this.state.currentDisplayMode == DisplayMode.SeparateAddressFields &&
          this.renderManualAddressControl()}
      </div>
    );
  };

  validate = (): boolean => {
    let isValid = true;
    let errors: string[] = [];

    if (this.props.allowInternationalAddress) {
      if (this.countrySelectorRef.current != null && !this.validateCountry()) {
        if (this.props.showDebug) {
          console.log(
            "AddressSelector::Validate():International addresses allowed, but country is not valid",
            this.state.address.country2CharIsoCode,
          );
        }
        isValid = false;
      }
    }

    if (
      validation.isEmpty(this.state.address.address1) ||
      this.state.address.address1 == undefined ||
      this.state.address.address1.trim().length < 2
    ) {
      this.setState({ showAddress1Error: true });
      errors.push("Address line 1 is invalid");
      isValid = false;
    } else {
      this.setState({ showAddress1Error: false });
    }

    if (this.state.address.address2 != null) {
      if (
        this.state.address.address2.trim().length > 0 &&
        this.state.address.address2.trim().length < 2
      ) {
        this.setState({ showAddress2Error: true });
        errors.push("Address line 2 is invalid");
        isValid = false;
      } else {
        this.setState({ showAddress2Error: false });
      }
    } else {
      this.setState({ showAddress2Error: false });
    }

    if (
      validation.isEmpty(this.state.address.city) ||
      this.state.address.city.trim().length < 2
    ) {
      this.setState({ showCityError: true });
      errors.push("City is invalid");
      isValid = false;
    } else {
      this.setState({ showCityError: false });
    }

    if (
      this.state.address.county == null ||
      validation.isEmpty(this.state.address.county) ||
      this.state.address.county.trim().length < 2
    ) {
      this.setState({ showCountyError: true });
      errors.push("County is invalid");
      isValid = false;
    } else {
      this.setState({ showCountyError: false });
    }

    // Check if this is a UK address (country code is GB or empty (default is GB))
    let ukAddress = false;
    if (
      this.state.address.country2CharIsoCode === "GB" ||
      this.state.address.country2CharIsoCode === ""
    ) {
      if (this.props.showDebug) {
        console.log(
          "AddressSelector::Validate():Uk address",
          this.state.address.country2CharIsoCode,
        );
      }
      ukAddress = true;
    }

    if (ukAddress) {
      // Check that if it's a UK address the post code has been entered and is correct
      if (
        ukAddress &&
        (validation.isEmpty(this.state.address.postCode) ||
          !validation.isUKPostcode(this.state.address.postCode))
      ) {
        if (this.props.showDebug) {
          console.log(
            "AddressSelector::Validate():postCode is empty or known to not be a UK postcode, even though we think this is a UK address",
          );
        }
        this.setState({ showPostCodeError: true });
        errors.push("Post code is invalid");
        isValid = false;
      } else {
        this.setState({ showPostCodeError: false });
        // block uk post code prefixes if required
        if (ukAddress && this.props.postCodePrefixesToBlock.length > 0) {
          let listOfPostCodePrefixes =
            this.props.postCodePrefixesToBlock.split(";");
          let blockApplied = false;

          for (let i = 0; i < listOfPostCodePrefixes.length; i++) {
            if (!blockApplied) {
              if (
                this.state.address.postCode.startsWith(
                  listOfPostCodePrefixes[i],
                )
              ) {
                // BLOCK
                if (typeof this.props.blockedPostCodeCallback === "function") {
                  this.props.blockedPostCodeCallback(
                    this.state.address.postCode,
                    listOfPostCodePrefixes[i],
                    "Post codes beginning with " +
                      listOfPostCodePrefixes[i] +
                      " are not permitted.",
                  );
                  this.setState({ showBlockedPostCodeError: true });
                  blockApplied = true;
                }
              }
            }
          }

          this.setState({ showBlockedPostCodeError: blockApplied });

          if (blockApplied) {
            this.setState({ results: [] });
            isValid = false;
          }
        }
      }
    } else {
      if (this.props.showDebug) {
        console.log(
          "AddressSelector::Non Uk address",
          this.state.address.country2CharIsoCode,
        );
      }
      this.setState({ showPostCodeError: false });
    }

    if (isValid) {
      if (this.props.showDebug) {
        console.log("AddressSelector::Validate():control is valid");
      }
      this.props.onValidAddress();
    } else {
      if (this.props.showDebug) {
        console.log("AddressSelector::Validate():Errors found", errors);
      }
      this.props.onInvalidAddress();
    }

    return isValid;
  };

  clear = async () => {
    await this.setState({ selectedAddress: new Address() });
    await this.setState({ addressSearchTerm: "" });
    await this.setState({ currentDisplayMode: DisplayMode.Lookup });
    this.fireDisplayModeChange(DisplayMode.Lookup);
  };

  componentDidMount() {
    if (this.props.showDebug) {
      console.log("AddressSelector::Mount():props", this.props);
      console.log("AddressSelector::Mount():state", this.props);
    }
    if (
      this.props.address &&
      this.props.address.address1 &&
      this.props.address.address1 !== ""
    ) {
      const addressString = this.formatAddress(this.props.address);

      if (this.props.showDebug) {
        console.log(
          "AddressSelector::Mount():address has been provided",
          this.props.address,
        );
        console.log("AddressSelector::Mount():addressString", addressString);
      }

      this.setState(
        {
          formattedAddress: addressString,
          selectedAddress: this.props.address,
          currentDisplayMode: DisplayMode.FormattedAddress,
          hasSelectedAddress: true,
        },
        () => {
          this.fireDisplayModeChange(DisplayMode.FormattedAddress);
        },
      );
    } else {
      if (this.props.showDebug) {
        console.log("AddressSelector::Mount():address has not been provided");
      }
      this.setState(
        {
          formattedAddress: "",
          selectedAddress: new Address(),
          address: new Address(),
          currentDisplayMode: DisplayMode.Lookup,
          hasSelectedAddress: false,
        },
        () => {
          this.fireDisplayModeChange(DisplayMode.Lookup);
        },
      );
    }
  }

  componentDidUpdate(
    prevProps: IAddressSelectorProps,
    prevState: IAddressSelectorState,
    snapshot: any,
  ) {}

  manuallySetAddress(newAddress: Address) {
    if (this.state.address != newAddress) {
      if (newAddress && newAddress.address1 && newAddress.address1 !== "") {
        const addressString = this.formatAddress(newAddress);

        if (this.props.showDebug) {
          console.log(
            "AddressSelector::Update():address has been provided",
            newAddress,
          );
          console.log("AddressSelector::Update():addressString", addressString);
        }

        this.setState(
          {
            formattedAddress: addressString,
            address: newAddress,
            selectedAddress: newAddress,
            currentDisplayMode: DisplayMode.FormattedAddress,
            hasSelectedAddress: true,
          },
          () => {
            this.fireDisplayModeChange(DisplayMode.FormattedAddress);
          },
        );
      }
    }
  }

  setIsInternational = async () => {
    await this.setState({ isInternationalAddress: true });
  };

  clearIsInternational = async () => {
    await this.setState({ isInternationalAddress: false });
  };

  formatAddress = (address: Address) => {
    return [
      address.address1,
      address.address2,
      address.city,
      address.county,
      address.postCode,
      address.country,
    ]
      .filter((x) => x != null && x !== "")
      .join("\n");
  };

  render() {
    return (
      <StyledAddressSelectorContainer
        className={
          (this.props.className !== "" ? this.props.className + " " : "") +
          "addressSelectorContainerOuter"
        }
      >
        {this.renderAddressSearchControl()}
      </StyledAddressSelectorContainer>
    );
  }
}

export default AddressSelector;
